import axios from 'axios';
import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import "bootstrap-icons/font/bootstrap-icons.css";
import { ROUTES } from '../Routes/routes';
import "../style/register.css";
import { motion } from 'framer-motion';
import { PredefinedWord } from '../Routes/Predefined';
import { toast } from "react-toastify";
import MapplsVectorMap from './Mapple';
import VectorMap from './DentistHub/components/VectorMap';
import { apiAxios } from '../utils/apiAxios';

interface Role {
  roleID: number;
  roleName: string;
}

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const MOBILE_REGEX = /^[6-9]\d{9}$/;
const EMAIL_REGEX = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
const PASSWORD_REGEX =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])[^\s]{6,14}$/;

const Register: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const preSelectedRole = (location.state as any)?.role || "";

  const [userType, setUserType] = useState("");
  const [roles, setRoles] = useState<Role[]>([]);

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [address, setAddress] = useState("");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");

  const [mobileError, setMobileError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [userTypeError, setUserTypeError] = useState("");
  const [emailError, setEmailError] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [loading, setLoading] = useState(false);

  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");

  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);

  const [timer, setTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);

  const passwordRef = useRef<HTMLDivElement | null>(null);
  const confirmPasswordRef = useRef<HTMLDivElement | null>(null);
  const [showMap, setShowMap] = useState(false);
  const [digiPin, setDigiPin] = useState("");
  /* -------------------- Duplicate Email -------------------- */
  const [emailChecking, setEmailChecking] = useState(false);
  const [emailExists, setEmailExists] = useState(false);
  const [confirmPasswordError, setConfirmPasswordError] = useState("");


  /* -------------------- COMPUTED -------------------- */
  const isMillingHub = useMemo(
    () =>
      roles.find(r => r.roleID.toString() === userType)?.roleName === "Milling Hub",
    [roles, userType]
  );
  /* -------------------- Duplicate Email Check -------------------- */
  const checkEmailExists = useCallback(async (emailValue: string) => {
    if (!EMAIL_REGEX.test(emailValue)) {
      setEmailError("Enter a valid email.");
      return;
    }

    try {
      setEmailChecking(true);
      const res = await apiAxios.get(`${API_BASE}/Users/CheckEmail?email=${emailValue}`);

      if (res.data?.exists) {
        setEmailError("Email already registered.");
        setEmailExists(true);
      } else {
        setEmailError("");
        setEmailExists(false);
      }
    } catch {
      setEmailError("Unable to verify email.");
    } finally {
      setEmailChecking(false);
    }
  }, []);

  /* -------------------- EFFECTS -------------------- */
  useEffect(() => {
    let interval: NodeJS.Timeout | undefined;

    if (otpSent && !canResend) {
      interval = setInterval(() => {
        setTimer(prev => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => interval && clearInterval(interval);
  }, [otpSent, canResend]);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const { data } = await apiAxios.get(`${API_BASE}/Users/GetRoles`);
        const allRoles: Role[] = data;

        if (preSelectedRole) {
          const match = allRoles.find(
            r => r.roleName.trim() === preSelectedRole
          );
          if (match) {
            setRoles([match]);
            setUserType(match.roleID.toString());
          }
        } else {
          setRoles(
            allRoles.filter(r =>
              ["Dentist", "Milling Hub"].includes(r.roleName.trim())
            )
          );
        }
      } catch (err) {
        console.warn("Role fetch failed", err);
      }
    };

    fetchRoles();
  }, [preSelectedRole]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (passwordRef.current && !passwordRef.current.contains(e.target as Node))
        setShowPassword(false);
      if (
        confirmPasswordRef.current &&
        !confirmPasswordRef.current.contains(e.target as Node)
      )
        setShowConfirmPassword(false);
    };

    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  /* -------------------- HELPERS -------------------- */
  const validatePassword = useCallback((pwd: string) => {
    return PASSWORD_REGEX.test(pwd)
      ? ""
      : "Password must include uppercase, lowercase, digit, special character, and have no spaces.";
  }, []);


  const handleSendOtp = useCallback(async () => {


    // ✅ Full Name validation
    if (!fullName.trim()) {
      toast.error("Please enter your full name.");
      return;
    }

    // ✅ Email validation
    if (!email.trim()) {
      toast.error("Email is required.");
      return;
    }

    if (!EMAIL_REGEX.test(email)) {
      setEmailError("Enter a valid email.");
      return;
    }

    // ✅ Email duplicate check
    if (emailExists) {
      setEmailError("Email already registered.");
      return;
    }

    if (!MOBILE_REGEX.test(mobile)) {
      setMobileError("Enter valid mobile number first.");
      return;
    }

    try {
      setSendingOtp(true);

      const response = await apiAxios.post(
        `${API_BASE}/OTP/send?mobile=${mobile}`
      );

      const expiryMinutes = response.data.expiryMinutes;

      setOtpSent(true);
      setOtp("");
      setTimer(expiryMinutes * 60);
      setCanResend(false);
      setOtpError("");

      // ✅ If devOtp exists (non-production), show it
      if (response.data.devOtp) {
        toast.info(`Dev OTP: ${response.data.devOtp}`);

        // Optional: Auto-fill OTP field
        setOtp(response.data.devOtp);

        // Optional: Browser Notification
        if ("Notification" in window) {
          Notification.requestPermission().then(permission => {
            if (permission === "granted") {
              new Notification("OTP Generated", {
                body: `Your OTP is ${response.data.devOtp}`
              });
            }
          });
        }
      } else {
        toast.success("OTP sent successfully!");
      }

    } catch {
      setOtpError("Failed to send OTP");
    } finally {
      setSendingOtp(false);
    }
  }, [mobile, fullName, email, emailExists]);


  const handleVerifyOtp = useCallback(async () => {
    if (!otp) {
      setOtpError("Please enter OTP.");
      return;
    }

    try {
      setVerifyingOtp(true);
      const { data } = await apiAxios.post(`${API_BASE}/Otp/verify`, {
        Mobile: mobile,
        OTP: otp
      });

      if (data?.valid) {
        setOtpVerified(true);
        setOtpError("");
        toast.success("OTP verified successfully!");
      } else {
        setOtpError("Invalid OTP! Please try again.");
      }
    } catch {
      setOtpError("Invalid OTP!");
    } finally {
      setVerifyingOtp(false);
    }
  }, [otp, mobile]);

  /* -------------------- REGISTER -------------------- */
  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!userType || userType === "0")
      return setUserTypeError("Please select a user type.");

    if (!MOBILE_REGEX.test(mobile))
      return setMobileError("Invalid mobile number.");

    if (!EMAIL_REGEX.test(email))
      return setEmailError("Enter a valid email.");

    // const pwdError = validatePassword(password);
    // if (pwdError) return setPasswordError(pwdError);

    const pwdError = validatePassword(password);
    if (pwdError) {
      setPasswordError(pwdError);
      return;
    } else {
      setPasswordError("");
    }

    if (password !== confirmPassword) {
      setConfirmPasswordError("Passwords do not match!");
      return;
    } else {
      setConfirmPasswordError("");
    }

    const payload: any = {
      Name: fullName,
      Email: email,
      MobileNo: mobile,
      RoleID: userType,
      PasswordHash: password,
      ConfirmPassword: confirmPassword
    };

    if (isMillingHub) {
      payload.Address = address;
      payload.Latitude = latitude;
      payload.Longitude = longitude;
      payload.DigiPin = digiPin;
    }

    try {
      setLoading(true);
      await apiAxios.post(`${API_BASE}/Users/CreateUser`, payload);
      navigate(ROUTES.LOGIN, { state: { email, password } });
    } catch (err: any) {
      toast.error(
        "Registration failed: " +
        (err.response?.data?.message || "Unexpected error")
      );
    } finally {
      setLoading(false);
    }
  };

  const handleShowLogin = () =>
    navigate(ROUTES.LOGIN, { state: { email } });

  const formVariant = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7 } },
  };

  const buttonVariant = {
    hover: { scale: 1.05, boxShadow: "0px 5px 15px rgba(0,0,0,0.2)" },
  };

  return (
    <div className="register-wrapper">
      {/* Background Animated Blobs */}
      <div className="login-bg">
        <motion.div className="bg-blob blob1" initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 1, delay: 0.2 }} />
        <motion.div className="bg-blob blob2" initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 1, delay: 0.4 }} />
        <motion.div className="bg-blob blob3" initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 1, delay: 0.6 }} />
      </div>

      {/* ONE SINGLE WHITE CARD */}
      <div className="register-card">

        {/* LEFT FORM */}
        <motion.div
          className="register-left register-back-tologin-card"
          variants={formVariant}
          initial="hidden"
          animate="visible"
        >

          <h2 className='mb-2'>
            Join Us <span>Today !</span>
          </h2>
          <br></br>
          <form onSubmit={handleRegisterSubmit}>

            {/* User Type */}
            {/* SHOW THESE ONLY BEFORE OTP VERIFIED */}
            {!otpVerified && (
              <>

                {/* User Type */}
                <div className="form-group customform-group">
                  <select
                    className="form-control register-dropdown-se customform-control"
                    value={userType}
                    onChange={(e) => setUserType(e.target.value)}
                    disabled={!!preSelectedRole || loading}
                  >
                    <option value="0">Select User Type</option>
                    {roles.map((role) => (
                      <option key={role.roleID} value={role.roleID}>
                        {role.roleName}
                      </option>
                    ))}
                  </select>
                  {userTypeError && <small className="error-text">{userTypeError}</small>}
                </div>

                {/* Full Name */}
                <div className="form-group customform-group">
                  <input
                    type="text"
                    className="form-control customform-control"
                    placeholder="Full Name"
                    value={fullName}
                    onChange={(e) =>
                      setFullName(e.target.value.replace(/\b\w/g, (c) => c.toUpperCase()))
                    }
                    required
                  />
                </div>

                {/* Email */}
                <div className="form-group customform-group">
                  <input
                    type="email"
                    className="form-control customform-control"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setEmailError("");
                      setEmailExists(false);
                    }}
                    onBlur={() => {
                      if (email.trim()) checkEmailExists(email.trim());
                    }}
                    required
                  />

                  {emailChecking && <small className="text-muted">Checking email...</small>}
                  {emailError && <small className="error-text">{emailError}</small>}
                </div>


                {/* Mobile */}
                <div className="form-group customform-group">
                  <input
                    type="text"
                    className="form-control customform-control"
                    placeholder="Mobile Number"
                    value={mobile}
                    maxLength={10}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, "");
                      setMobile(value);
                      if (MOBILE_REGEX.test(value)) {
                        setMobileError("");
                      }
                    }}
                    required
                  />

                  {mobileError && (
                    <small className="error-text">{mobileError}</small>
                  )}
                </div>


                {/* OTP Section */}
                <div className="form-group mt-4">
                  {!otpSent && (
                 <button
  type="button"
  className="btn verify-otpbtn w-100"
  onClick={handleSendOtp}
  disabled={sendingOtp || !userType || userType === "0"}
>
  {sendingOtp ? "Sending OTP..." : "Send OTP"}
</button>
                  )}


                  {otpSent && !otpVerified && !canResend && (
                    <>
                      <input
                        type="text"
                        className="form-control mt-2"
                        placeholder="Enter OTP"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                        maxLength={6}
                      />
                      {otpError && <small className="text-danger">{otpError}</small>}

                      <button
                        type="button"

                        className="btn verify-otpbtn w-100 mt-3"
                        onClick={handleVerifyOtp}
                        disabled={verifyingOtp}
                      >
                        {verifyingOtp ? "Verifying..." : "Verify OTP"}
                      </button>

                      <p className="text-muted text-center mt-2">
                        Resend OTP in <b>
                          {timer >= 60
                            ? `${Math.floor(timer / 60)}m ${timer % 60}s`
                            : `${timer}s`}
                        </b>
                      </p>

                    </>
                  )}

                  {otpSent && !otpVerified && canResend && (
                    <button
                      type="button"
                      className="btn btn-primary w-100 mt-2"
                      onClick={handleSendOtp}
                      disabled={sendingOtp}
                    >
                      {sendingOtp ? "Sending OTP..." : "Resend OTP"}
                    </button>
                  )}
                  <motion.p
                    className="backtologin btn-block mt-3 d-flex justify-content-center align-items-center gap-1"
                    onClick={handleShowLogin}
                    variants={buttonVariant}
                    style={{ cursor: "pointer" }}
                  >
                    ← Back to Login
                  </motion.p>
                </div>
              </>
            )}



            {/* Milling Hub Extra Fields */}
            {isMillingHub && otpVerified && (
              <>

                <div className="form-group customform-group">
                  <input
                    type="text"
                    className="form-control customform-control"
                    placeholder="Address"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    required={isMillingHub}
                  />
                </div>

                <div className="form-group customform-group">
                  <input
                    type="text"
                    className="form-control customform-control"
                    placeholder="Latitude"
                    value={latitude}
                    onChange={(e) => setLatitude(e.target.value)}
                    required={isMillingHub}
                    readOnly
                  />
                </div>

                <div className="form-group customform-group">
                  <input
                    type="text"
                    className="form-control customform-control"
                    placeholder="Longitude"
                    value={longitude}
                    onChange={(e) => setLongitude(e.target.value)}
                    required={isMillingHub}
                    readOnly
                  />
                </div>

              </>
            )}



            {otpVerified && (
              <>
                {/* Password */}
                <div className='row'>
                  <div className='col-md-12'>
                    <div
                      className="form-group customform-group password-group"
                      ref={passwordRef}

                    >
                      <input
                        type={showPassword ? "text" : "password"}
                        className="form-control customform-control"
                        placeholder="Password"
                        value={password}
                        onChange={(e) => {
                          setPassword(e.target.value)
                          setPasswordError("");
                        }}
                        required
                      />
                      <span className="password-toggle" onClick={() => setShowPassword(!showPassword)}>
                        <i className={`bi ${showPassword ? "bi-eye" : "bi-eye-slash"}`}></i>
                      </span>
                    </div>
                    {showPassword && (
                      <div className="password-tooltip">
                        Password must have at least one digit, one lowercase, one uppercase, one special character. 8–14 characters. No spaces.
                      </div>
                    )}

                    {passwordError && <small className="error-text">{passwordError}</small>}
                  </div>

                  {/* Confirm Password */}
                  <div className='col-md-12'>
                    <div className="form-group customform-group password-group" ref={confirmPasswordRef}>
                      <div className="password-input-wrapper">
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          className="form-control customform-control"
                          placeholder="Confirm Password"
                          value={confirmPassword}
                          onChange={(e) => {
                            setConfirmPassword(e.target.value)
                            setConfirmPasswordError("");
                          }}
                          required
                        />

                        <span
                          className="password-toggle confirm-toggle-btn"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        >
                          <i className={`bi ${showConfirmPassword ? "bi-eye" : "bi-eye-slash"}`}></i>
                        </span>
                      </div>

                      {confirmPasswordError && (
                        <small className="error-text">{confirmPasswordError}</small>
                      )}
                    </div>

                  </div>
                </div>
                {/* Buttons */}
                <div className='d-flex gap-4 mt-3 justify-content-center'>
                  <motion.button
                    type="submit"
                    className="btn btn-login register-nowbtn"
                    variants={buttonVariant}
                  >
                    {loading ? "Registering..." : "Register"}
                  </motion.button>

                  <motion.p
                    className="backtologin btn-block mt-3 d-flex align-items-center gap-1"
                    onClick={handleShowLogin}
                    variants={buttonVariant}
                    style={{ cursor: "pointer" }}
                  >
                    ← Back to Login
                  </motion.p>
                </div>
              </>
            )}

          </form>

        </motion.div>

        {/* RIGHT IMAGE SECTION */}
        {isMillingHub && otpVerified && (
          <motion.div
            className="register-right mx-3 mt-5 pt-4"
            initial={{ x: 100, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <VectorMap provider="google" onConfirm={(data: any) => {
              setLatitude(data.latitude.toString());
              setLongitude(data.longitude.toString());
              setDigiPin(data.digiPin);
              setAddress(data.address);
              setShowMap(false);
            }} />

            {/* <MapplsVectorMap
              onConfirm={(data: any) => {
                setLatitude(data.latitude.toString());
                setLongitude(data.longitude.toString());
                setDigiPin(data.digiPin);
                setAddress(data.address);
                setShowMap(false);
              }}
            /> */}
          </motion.div>
        )}
        {!otpVerified && (
          <motion.div
            className="register-right"
            initial={{ x: 100, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >

            {/* Floating Shapes */}
            <span className="float-circle circle-1"></span>
            <span className="float-circle circle-2"></span>
            <span className="float-circle circle-3"></span>
            <span className="float-circle circle-4"></span>
            <span className="float-circle circle-5"></span>
            <span className="float-circle circle-6"></span>
            <span className="float-circle circle-7"></span>
            <span className="float-circle circle-8"></span>

            {/* Image */}
            <img
              src={PredefinedWord.images.rightbgimage}
              alt={PredefinedWord.images.rightbgimagealt}
              className="register-image"
            />
          </motion.div>
        )}
      </div>
    </div>

  );
};

export default Register;



